import React from "react";
import { useSelector } from "react-redux";
import domScrollTo from "../utils/domScrollTo";
import addLineBreaks from "../utils/addLineBreaks";
import { PRICE_MODE_MAX } from "../ducks/shoppingCartSlice";
import LoadingWall from "../components/LoadingWall";
import QuestionsCards from "../containers/QuestionsCards";
import ProductTopBar from "./ProductTopBar";
import Loading from "../components/Loading";
import FloatingProductOrderFooter from "../containers/FloatingProductOrderFooter";
import { STATE_STEP } from "../ducks/consts";
import useMPSecurity from "../hooks/useMPSecurity";
import useProductOrderSession from "../hooks/useProductOrderSession";
import ProductPriceLabel from "../components/ProductPriceLabel";
import BestSellerBadge from "../components/BestSellerBadge";
import ProductWithQuestionsTotem from "./ProductWithQuestionsTotem";
import { selectLayout } from "../ducks/shopSelectors";

export default function ProductWithQuestions({
  productId,
  product,
  orderUuidEdit,
}) {
  const layout = useSelector(selectLayout);
  const { isAvailableToOrder, messageToOrder, pizzaPriceMode, mercadoPagoKey } =
    useSelector((state) => state.shop);
  useMPSecurity(mercadoPagoKey, "item");

  const {
    orderUuid,
    stateStep,
    alredyAnswerCombo,
    isOrderVisible,
    returnPath,
    onBack,
  } = useProductOrderSession({ productId, orderUuidEdit });

  React.useEffect(() => {
    domScrollTo(0, "auto");
  }, []);

  if (!orderUuid) {
    return <LoadingWall />;
  }

  if (layout.product === "totem") {
    return (
      <ProductWithQuestionsTotem
        onBack={onBack}
        product={product}
        pizzaPriceMode={pizzaPriceMode}
        isOrderVisible={isOrderVisible}
        orderUuid={orderUuid}
        orderUuidEdit={orderUuidEdit}
      />
    );
  }

  return (
    <>
      <div className="product-page">
        <ProductTopBar
          title={product.name}
          onBack={onBack}
          hasHero={!!product.pictureUrl}
          fallbackPath={returnPath}
        />
        <ProductInfo product={product} pizzaPriceMode={pizzaPriceMode} />
        <MessageToOrder
          isAvailableToOrder={isAvailableToOrder}
          messageToOrder={messageToOrder}
        />
        {(stateStep === STATE_STEP.LOADING || !alredyAnswerCombo) && (
          <Loading message="Carregando opções..." />
        )}
        {stateStep === STATE_STEP.READY && alredyAnswerCombo === true && (
          <QuestionsCards
            isAvailableToOrder={isAvailableToOrder}
            productId={productId}
            orderUuid={orderUuid}
          />
        )}
        {stateStep === STATE_STEP.ERROR && (
          <div className="empty-state">
            <p>Erro inesperado, tente novamente mais tarde.</p>
          </div>
        )}
      </div>
      {isOrderVisible && (
        <FloatingProductOrderFooter
          isOrderVisible={isOrderVisible}
          orderUuid={orderUuid}
          orderUuidEdit={orderUuidEdit}
          returnPath={returnPath}
        />
      )}
    </>
  );
}

const ProductInfo = ({ product, pizzaPriceMode }) => (
  <>
    {product.pictureUrl && (
      <div className="product-hero">
        <img src={product.pictureUrl} alt="" className="product-hero__img" />
      </div>
    )}
    <div
      className={`product-info${product.pictureUrl ? "" : " product-info--no-hero"}`}
    >
      <h1 className="product-info__name">{product.name}</h1>
      {(product.isBestSeller || product.disabled) && (
        <div className="product-info__badges">
          {product.isBestSeller && <BestSellerBadge />}
          {product.disabled && (
            <span className="tag tag--muted">Indisponível</span>
          )}
        </div>
      )}
      {product.details && (
        <p className="product-info__details">
          {addLineBreaks(product.details)}
        </p>
      )}
      <p className="product-info__price">
        <ProductPriceLabel product={product} />
      </p>
      {product.pizza && pizzaPriceMode === PRICE_MODE_MAX && (
        <p className="product-info__note">
          A pizza com vários sabores é cobrada pelo preço do sabor mais caro.
        </p>
      )}
    </div>
  </>
);

const MessageToOrder = ({ isAvailableToOrder, messageToOrder }) => {
  return isAvailableToOrder || !messageToOrder ? null : (
    <div className="notice notice--info">
      <span>{messageToOrder}</span>
    </div>
  );
};
